import { GitBranch, X } from 'lucide-react';
import type { FSM } from '../types';

export function analyzeFsmReachability(fsm: FSM) {
  const start = fsm.resetState ?? fsm.states[0]?.name;
  const constantFalse = (condition: string) => /^(?:1'b0|1'h0|0|false)$/i.test(condition.trim().replace(/[()]/g, ''));
  const ignoredTransitions = fsm.transitions.filter((transition) => constantFalse(transition.condition));
  const reachable = new Set<string>(start ? [start] : []);
  const queue = start ? [start] : [];

  while (queue.length) {
    const current = queue.shift()!;
    fsm.transitions.forEach((transition) => {
      if (transition.source !== current || constantFalse(transition.condition) || reachable.has(transition.target)) return;
      reachable.add(transition.target);
      queue.push(transition.target);
    });
  }

  return {
    unreachable: fsm.states.map((state) => state.name).filter((state) => !reachable.has(state)),
    deadEnds: fsm.states.map((state) => state.name).filter((state) => !fsm.transitions.some((transition) => transition.source === state && transition.target !== state && !constantFalse(transition.condition))),
    ignoredTransitions,
  };
}

export function FsmMiniViewer({ fsm, close }: { fsm: FSM; close: () => void }) {
  const analysis = analyzeFsmReachability(fsm);
  return <aside className="fsm-mini-viewer" aria-label={`${fsm.name} state viewer`}>
    <header><span><GitBranch size={13} /> STATE VIEW</span><b>{fsm.stateSignal}</b><button onClick={close} aria-label="Close state viewer"><X size={13} /></button></header>
    <div className="fsm-state-track">
      {fsm.states.map((state, index) => <div className="fsm-state-step" key={state.id}>
        <span className={state.name === fsm.resetState ? 'is-reset' : ''}><i>{state.value ?? index}</i><b>{state.name}</b></span>
        {index < fsm.states.length - 1 && <em>→</em>}
      </div>)}
    </div>
    <div className="fsm-transition-list">
      {fsm.transitions.slice(0, 5).map((transition, index) => <code key={`${transition.source}-${transition.target}-${index}`}><span>{transition.source}</span><b>→</b><span>{transition.target}</span><em>{transition.condition}</em></code>)}
    </div>
    <div className="fsm-analysis-hints" aria-label="Heuristic FSM analysis">
      {analysis.unreachable.length ? <p className="is-warning"><b>UNREACHABLE</b><span>{analysis.unreachable.join(', ')}</span></p> : <p className="is-clear"><b>REACHABILITY</b><span>All states reachable from reset</span></p>}
      {!!analysis.deadEnds.length && <p><b>NO EXIT PATH</b><span>{analysis.deadEnds.join(', ')}</span></p>}
      {!!analysis.ignoredTransitions.length && <p><b>CONSTANT FALSE</b><span>{analysis.ignoredTransitions.map((transition) => `${transition.source}→${transition.target}`).join(', ')}</span></p>}
      <small>Static heuristic · review conditions in RTL</small>
    </div>
    <footer><span className="fsm-reset-key" /> reset state <b>{fsm.states.length} STATES</b></footer>
  </aside>;
}
